import { css, cx, useTheme } from '@fable/theme'
import { useStatsModal } from '../../../lib/ctx/stats_modal'
import { useScreenshot } from 'components/screenshot/lib'
import { StatsScreenshot } from './StatsScreenshot'
import { useRef, useState } from 'react'
import { CompChildren, CompProps } from '@fable/types'
import { FlexBox, Icon, Loader, LoaderType } from '@fable/components'
import { delay } from '@fable/utils'
import { useStatsTheme } from '../../../theme/ctx'

interface Props {
  className?: string
  iconColor?: string
  content?: CompChildren
  screenshotStyles?: string
  onClick?: () => void
  dev?: boolean
}

export const StatsScreenshotButton = ({
  children,
  className = '',
  iconColor,
  content,
  screenshotStyles = '',
  onClick,
  dev = false,
}: CompProps<Props>) => {
  const ref = useRef<HTMLDivElement>(null)
  const { nativeOverrides } = useTheme()
  const theme = useStatsTheme((s) => s.theme)
  const [isOpen, open] = useStatsModal((s) => [s.isOpen, s.open])
  const [showScreenshot, setShowScreenshot] = useState(false)
  const { takeScreenshot, capturing } = useScreenshot()
  const buttonIconColor = iconColor || theme.colors.section.shareText
  const isLoading = capturing || showScreenshot

  const handleTakeScreenshot = async () => {
    if (isOpen) return

    onClick?.()
    setShowScreenshot(true)

    await delay(300)

    if (!ref.current) return

    const image = await takeScreenshot(ref.current, {
      width: 380,
    })

    // TODO: get BG gradients without corner radius !!!!

    open(
      <div
        className={css`
          padding-bottom: 20px;
        `}
      >
        <img
          className={css`
            display: block;
            width: 100%;
            border-radius: 24px;
            border: 2px solid
              ${theme.id.includes('dark')
                ? 'rgba(255, 255, 255, 0.2)'
                : 'transparent'};
            overflow: hidden;
          `}
          src={image}
          alt="screenshot"
        />
      </div>
    )

    setShowScreenshot(false)
  }

  const getContent = () => {
    if (isLoading) {
      return (
        <Loader
          className={css`
            width: 100%;
            svg {
              width: 16px !important;
            }
            svg path {
              fill: ${buttonIconColor};
            }
          `}
          type={LoaderType.contained}
        />
      )
    }

    if (content) return content

    return (
      <Icon
        className={css`
          width: 16px;
        `}
        type="share"
        fill={buttonIconColor}
      />
    )
  }

  return (
    <>
      <button
        data-testid="stats-screenshot-button"
        className={cx(
          css`
            ${nativeOverrides.buttonClear};
            width: 32px;
            height: 32px;
            border-radius: 50%;
            background-color: ${theme.colors.section.shareBg};
            position: relative;
          `,
          className
        )}
        onClick={handleTakeScreenshot}
      >
        <FlexBox centerAll>{getContent()}</FlexBox>
      </button>
      {showScreenshot ? (
        <StatsScreenshot ref={ref} className={screenshotStyles} dev={dev}>
          {children}
        </StatsScreenshot>
      ) : null}
    </>
  )
}
